import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { format } from 'date-fns'
import { StandardColumn } from './types'
import { ColumnsPreference } from '../../contexts/UserPreferencesContext'
import { orderVisibleColumns, resolveColumnLabel } from './columnUtils'

interface ExportToExcelOptions<T> {
  fileName: string
  sheetName?: string
  columns: StandardColumn<T>[]
  columnsState: ColumnsPreference
  rows: T[]
}

const EPOCH_MS_MIN = 1e11
const EPOCH_MS_MAX = 1e13
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/

const formatDateForExport = (date: Date): string => {
  if (Number.isNaN(date.getTime())) return ''
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0
  return format(date, hasTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
}

const resolveExportValue = <T,>(column: StandardColumn<T>, row: T): string | number => {
  if (column.exportValue) {
    const value = column.exportValue(row)
    return value ?? ''
  }
  const raw = column.accessor ? column.accessor(row) : (row as Record<string, unknown>)?.[column.id]
  if (raw === null || raw === undefined) return ''
  if (raw instanceof Date) return formatDateForExport(raw)
  if (typeof raw === 'number') {
    if (raw >= EPOCH_MS_MIN && raw < EPOCH_MS_MAX) return formatDateForExport(new Date(raw))
    return raw
  }
  if (typeof raw === 'string') {
    if (ISO_DATE_ONLY_REGEX.test(raw)) {
      const [year, month, day] = raw.split('-')
      return `${day}/${month}/${year}`
    }
    if (ISO_DATE_TIME_REGEX.test(raw)) return formatDateForExport(new Date(raw))
    return raw
  }
  if (typeof raw === 'boolean') return raw ? 'Sim' : 'Não'
  return String(raw)
}

export async function exportTableToExcel<T>({
  fileName,
  sheetName = 'Dados',
  columns,
  columnsState,
  rows,
}: ExportToExcelOptions<T>) {
  const visibleColumns = orderVisibleColumns(columns, columnsState)
  const exportColumns: StandardColumn<T>[] = []
  const addedIds = new Set<string>()

  for (const col of visibleColumns) {
    if (col.excludeFromExport) {
      const subCols = columns.filter((c) => c.parentId === col.id && !c.excludeFromExport)
      for (const sub of subCols) {
        if (!addedIds.has(sub.id)) {
          exportColumns.push(sub)
          addedIds.add(sub.id)
        }
      }
    } else {
      if (!addedIds.has(col.id)) {
        exportColumns.push(col)
        addedIds.add(col.id)
      }
    }
  }

  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(sheetName)

  sheet.columns = exportColumns.map((col) => ({
    header: resolveColumnLabel(col),
    key: col.id,
    width: 22,
  }))

  const headerRow = sheet.getRow(1)
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0090D3' } }
  headerRow.alignment = { vertical: 'middle' }
  headerRow.height = 20

  rows.forEach((row) => {
    sheet.addRow(Object.fromEntries(exportColumns.map((col) => [col.id, resolveExportValue(col, row)])))
  })

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${fileName}.xlsx`,
  )
}
